window.MTM_SAMPLE_VARIANTS = window.MTM_SAMPLE_VARIANTS || {};

if (!customElements.get('mtm-calculator')) {
  class MtmCalculator extends HTMLElement {
    constructor() {
      super();

      this.config = this.getConfig();
      this.form = document.getElementById(this.dataset.productFormId);
      this.widthInput = this.querySelector('[data-mtm-width]');
      this.dropInput = this.querySelector('[data-mtm-drop]');
      this.headingInputs = this.querySelectorAll('[data-mtm-heading]');
      this.eyeletColourGroup = this.querySelector('[data-mtm-eyelet-colour-group]');
      this.eyeletColourInputs = this.querySelectorAll('[data-mtm-eyelet-colour]');
      this.fitPositionInputs = this.querySelectorAll('[data-mtm-fit-position]');
      this.fittingTypeInputs = this.querySelectorAll('[data-mtm-fitting-type]');
      this.liningInputs = this.querySelectorAll('[data-mtm-lining]');
      this.controlSideInputs = this.querySelectorAll('[data-mtm-control-side]');
      this.pairingInputs = this.querySelectorAll('[data-mtm-pairing]');
      this.priceEl = this.querySelector('[data-mtm-price]');
      this.comparePriceEl = this.querySelector('[data-mtm-compare-price]');
      this.savingEl = this.querySelector('[data-mtm-saving]');
      this.promotionEl = this.querySelector('[data-mtm-promotion]');
      this.priceStatusEl = this.querySelector('[data-mtm-price-status]');
      this.customQuoteButton = this.querySelector('[data-mtm-custom-quote-button]');
      this.customQuoteForm = this.querySelector('[data-mtm-custom-quote-form]');
      this.customQuoteSummary = this.querySelector('[data-mtm-custom-quote-summary]');
      this.widthRangeEl = this.querySelector('[data-mtm-width-range]');
      this.dropRangeEl = this.querySelector('[data-mtm-drop-range]');
      this.priceProperty = this.querySelector('[data-mtm-price-property]');
      this.unitInput = this.querySelector('[data-mtm-unit]');
      this.calculatorTypeInput = this.querySelector('[data-mtm-calculator-type]');
      this.quoteIdInput = this.querySelector('[data-mtm-quote-id]');
      this.quoteTokenInput = this.querySelector('[data-mtm-quote-token]');
      this.expiresAtInput = this.querySelector('[data-mtm-expires-at]');
      this.retailPriceInput = this.querySelector('[data-mtm-retail-price]');
      this.compareAtPriceInput = this.querySelector('[data-mtm-compare-at-price]');
      this.savingInput = this.querySelector('[data-mtm-saving-property]');
      this.pricingBasisInput = this.querySelector('[data-mtm-pricing-basis]');
      this.messageEl = this.querySelector('[data-mtm-message]');
      this.submitButton = this.querySelector('[data-mtm-submit]');
      this.buttonNote = this.querySelector('[data-mtm-button-note]');
      this.sampleButtons = this.querySelectorAll('[data-mtm-sample-action]');
      this.choiceGroups = this.querySelectorAll('[data-mtm-choice-group]');
      this.measuringPrompt = this.querySelector('[data-mtm-measuring-prompt]');
      this.measuringFitVideos = this.querySelectorAll('[data-mtm-measuring-fit-video]');
      this.quoteApiUrl = (this.dataset.quoteApiUrl || this.config.quoteApiUrl || '').trim();
      this.createCartItemUrl = this.getCreateCartItemUrl();
      this.quote = null;
      this.quoteRequestTimer = null;
      this.quoteAbortController = null;
      this.legacyCalculatorObserver = null;
      this.submitIsLoading = false;
      this.productType = this.config.productType || this.dataset.productType || 'curtain';

      this.moneyFormatter = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: this.config.currency || 'GBP'
      });

      this.removeLegacyCalculator();
      this.watchLegacyCalculatorInjection();
      this.bindEvents();
      this.applyConfig();
      this.updateChoiceSummaries();
      this.updateMeasuringHelpVideos();
      this.scheduleQuoteUpdate();
      this.revealPostedCustomQuoteForm();
    }

    getConfig() {
      const configEl = this.querySelector('[data-mtm-config]');
      if (!configEl) return {};

      try {
        return JSON.parse(configEl.textContent);
      } catch (error) {
        console.warn('Made-to-measure calculator config is invalid.', error);
        return {};
      }
    }

    getCreateCartItemUrl() {
      const configuredUrl = (this.dataset.cartItemApiUrl || this.config.cartItemApiUrl || '').trim();
      if (configuredUrl) return configuredUrl;
      if (!this.quoteApiUrl) return '';
      return this.quoteApiUrl.replace(/\/quote\/?$/, '/create-cart-item');
    }

    disconnectedCallback() {
      if (this.legacyCalculatorObserver) this.legacyCalculatorObserver.disconnect();
    }

    removeLegacyCalculator() {
      this.querySelectorAll('#calculator[data-calcid], input.calc-prop, #cpcPriceToken, .loading-anim-cpc').forEach((element) => {
        const calculator = element.closest('#calculator[data-calcid]');
        (calculator || element).remove();
      });
    }

    watchLegacyCalculatorInjection() {
      this.legacyCalculatorObserver = new MutationObserver(() => this.removeLegacyCalculator());
      this.legacyCalculatorObserver.observe(this, { childList: true, subtree: true });
    }

    bindEvents() {
      [this.widthInput, this.dropInput].forEach((input) => {
        if (input) input.addEventListener('input', this.scheduleQuoteUpdate.bind(this));
        if (input) input.addEventListener('change', this.scheduleQuoteUpdate.bind(this));
        if (input) input.addEventListener('wheel', this.preventMeasurementWheelChange, { passive: false });
      });

      [...this.headingInputs, ...this.eyeletColourInputs, ...this.fitPositionInputs, ...this.fittingTypeInputs, ...this.liningInputs, ...this.controlSideInputs, ...this.pairingInputs].forEach((input) => {
        input.addEventListener('change', this.handleOptionChange.bind(this));
      });

      this.choiceGroups.forEach((group) => {
        const toggle = group.querySelector('[data-mtm-choice-toggle]');
        if (toggle) toggle.addEventListener('click', () => this.setChoiceGroupExpanded(group, true));
      });

      if (this.submitButton) {
        this.submitButton.addEventListener('click', async (evt) => {
          evt.preventDefault();

          if (!this.validate()) {
            this.reportValidity();
            return;
          }

          await this.addMtmQuoteToCart();
        });
      }

      this.sampleButtons.forEach((button) => {
        button.addEventListener('click', this.handleSampleAction.bind(this));
      });

      if (this.customQuoteButton) {
        this.customQuoteButton.addEventListener('click', () => this.expandCustomQuoteForm());
      }
    }

    preventMeasurementWheelChange(evt) {
      if (document.activeElement === evt.currentTarget) {
        evt.preventDefault();
      }
    }

    revealPostedCustomQuoteForm() {
      if (!this.customQuoteForm?.querySelector('.alert')) return;
      this.customQuoteForm.hidden = false;
      this.customQuoteForm.classList.remove('is-hidden');
      if (this.customQuoteButton) this.customQuoteButton.hidden = true;
    }

    applyConfig() {
      if (this.config.unit && this.unitInput) this.unitInput.value = this.config.unit;
      if (this.config.calculatorType && this.calculatorTypeInput) {
        this.calculatorTypeInput.value = this.config.calculatorType;
      }

      this.querySelectorAll('[data-mtm-unit-label]').forEach((label) => {
        label.textContent = this.config.unit || 'cm';
      });

      if (this.widthInput) {
        this.widthInput.min = this.config.minWidth || 0;
        this.widthInput.max = this.config.maxWidth || '';
        if (this.config.defaultWidth) this.widthInput.value = this.config.defaultWidth;
      }

      if (this.dropInput) {
        this.dropInput.min = this.config.minDrop || 0;
        this.dropInput.max = this.config.maxDrop || '';
        if (this.config.defaultDrop) this.dropInput.value = this.config.defaultDrop;
      }

      this.updateRangeText();
      this.updateEyeletColourVisibility();

      const sample = this.config.sample || {};
      this.querySelector('[data-mtm-sample-label]')?.replaceChildren(sample.label || 'Free fabric sample');
      this.querySelector('[data-mtm-sample-fabric]')?.replaceChildren(sample.fabricName || '');
      this.querySelector('[data-mtm-sample-code]')?.replaceChildren(sample.fabricCode || '');
    }

    updateRangeText() {
      const unit = this.config.unit || 'cm';
      if (this.widthRangeEl && this.config.minWidth && this.config.maxWidth) {
        this.widthRangeEl.textContent = `Width: ${this.config.minWidth}${unit} - ${this.config.maxWidth}${unit}`;
      }

      if (this.dropRangeEl && this.config.minDrop && this.config.maxDrop) {
        this.dropRangeEl.textContent = `Drop: ${this.config.minDrop}${unit} - ${this.config.maxDrop}${unit}`;
      }
    }

    handleOptionChange(evt) {
      if (evt.currentTarget.matches('[data-mtm-heading]')) {
        this.updateEyeletColourVisibility();
      }

      if (evt.currentTarget.matches('[data-mtm-fit-position]')) {
        this.updateMeasuringHelpVideos();
      }

      const group = evt.currentTarget.closest('[data-mtm-choice-group]');
      if (group && evt.currentTarget.checked) {
        this.updateChoiceSummary(group);
        this.setChoiceGroupExpanded(group, false);
      }

      this.scheduleQuoteUpdate();
    }

    updateMeasuringHelpVideos() {
      if (!this.isRomanBlind() || this.measuringFitVideos.length === 0) return;

      const selectedFit = this.getCheckedValue(this.fitPositionInputs);
      const hasSelectedFitVideo = selectedFit === 'recess' || selectedFit === 'exact';

      if (this.measuringPrompt) this.measuringPrompt.hidden = hasSelectedFitVideo;

      this.measuringFitVideos.forEach((panel) => {
        const isActive = panel.dataset.mtmMeasuringFitVideo === selectedFit;
        panel.hidden = !isActive;
        if (!isActive) {
          panel.querySelector('video')?.pause();
        }
      });
    }

    updateEyeletColourVisibility() {
      const shouldShow = this.getCheckedValue(this.headingInputs) === 'eyelet';
      if (!this.eyeletColourGroup) return;

      this.eyeletColourGroup.hidden = !shouldShow;
      this.eyeletColourInputs.forEach((input) => {
        input.disabled = !shouldShow;
        input.required = shouldShow;
        if (!shouldShow) input.checked = false;
      });

      this.updateChoiceSummary(this.eyeletColourGroup);
      this.setChoiceGroupExpanded(this.eyeletColourGroup, shouldShow);
    }

    updateChoiceSummaries() {
      this.choiceGroups.forEach((group) => {
        this.updateChoiceSummary(group);
        this.setChoiceGroupExpanded(group, true);
      });
    }

    updateChoiceSummary(group) {
      const selected = group.querySelector('input[type="radio"]:checked');
      const toggle = group.querySelector('[data-mtm-choice-toggle]');
      const valueEl = group.querySelector('[data-mtm-choice-value]');
      if (!toggle || !valueEl) return false;

      const value = selected ? this.getOptionLabel(selected) : '';
      valueEl.textContent = value;
      toggle.hidden = !value;

      return Boolean(value);
    }

    setChoiceGroupExpanded(group, expanded) {
      const hasSelection = this.updateChoiceSummary(group);
      const shouldExpand = expanded || !hasSelection;
      const toggle = group.querySelector('[data-mtm-choice-toggle]');
      const options = group.querySelector('[data-mtm-choice-options]');

      if (toggle) {
        toggle.hidden = !hasSelection || shouldExpand;
        toggle.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
      }

      if (options) options.hidden = !shouldExpand;
      group.classList.toggle('is-collapsed', !shouldExpand);
    }

    getOptionLabel(input) {
      if (!input) return '';
      if (input.dataset.mtmOptionLabel) return input.dataset.mtmOptionLabel;

      const label = [...this.querySelectorAll('label')].find((item) => item.htmlFor === input.id);
      return label?.querySelector('.mtm-calculator__option-title')?.textContent.trim()
        || label?.textContent.trim()
        || input.value;
    }

    scheduleQuoteUpdate() {
      this.clearFieldErrors();
      this.clearQuote();

      window.clearTimeout(this.quoteRequestTimer);

      if (!this.quoteApiUrl) {
        this.setPriceUnavailable('Quote API not configured', 'Add the private quote API URL in the MTM calculator block settings to show real pricing.');
        return;
      }

      if (!this.hasValidQuoteInputs()) {
        this.setPriceUnavailable('Price available after choices', this.getIncompleteQuoteMessage());
        return;
      }

      this.setPriceLoading();
      this.quoteRequestTimer = window.setTimeout(() => this.fetchQuote(), 350);
    }

    getCheckedValue(inputs) {
      return [...inputs].find((input) => input.checked)?.value || '';
    }

    validate() {
      this.hideMessage();
      this.clearFieldErrors();

      const errors = [];
      this.validateMeasurement(this.widthInput, this.config.minWidth, this.config.maxWidth, 'Width', errors);
      this.validateMeasurement(this.dropInput, this.config.minDrop, this.config.maxDrop, 'Drop', errors);

      if (this.isRomanBlind()) {
        if (!this.getCheckedValue(this.fitPositionInputs)) errors.push('Please choose a fit position.');
        if (!this.getCheckedValue(this.fittingTypeInputs)) errors.push('Please choose a fitting type.');
        if (!this.getCheckedValue(this.controlSideInputs)) errors.push('Please choose a control side.');
      } else {
        if (!this.getCheckedValue(this.headingInputs)) errors.push('Please choose a heading style.');
        if (this.getCheckedValue(this.headingInputs) === 'eyelet' && !this.getCheckedValue(this.eyeletColourInputs)) {
          errors.push('Please choose an eyelet colour.');
        }
        if (!this.getCheckedValue(this.pairingInputs)) errors.push('Pair pricing is currently the only online option.');
      }
      if (!this.getCheckedValue(this.liningInputs)) errors.push('Please choose a lining.');

      if (errors.length > 0) {
        this.showMessage(errors.join(' '));
        return false;
      }

      return true;
    }

    validateMeasurement(input, min, max, label, errors) {
      if (!input) return;

      const value = Number.parseFloat(input.value);
      if (!input.value || Number.isNaN(value)) {
        input.setCustomValidity(`Please enter a ${label.toLowerCase()}.`);
        errors.push(`Please enter a ${label.toLowerCase()}.`);
        return;
      }

      if (value < min || value > max) {
        const unit = this.config.unit || 'cm';
        input.setCustomValidity(`${label} must be between ${min}${unit} and ${max}${unit}.`);
        errors.push(`${label} must be between ${min}${unit} and ${max}${unit}.`);
        return;
      }

      input.setCustomValidity('');
    }

    clearFieldErrors() {
      [this.widthInput, this.dropInput, ...this.headingInputs, ...this.eyeletColourInputs, ...this.fitPositionInputs, ...this.fittingTypeInputs, ...this.liningInputs, ...this.controlSideInputs, ...this.pairingInputs].forEach((input) => {
        if (input) input.setCustomValidity('');
      });
    }

    reportValidity() {
      const firstInvalid = [this.widthInput, this.dropInput, ...this.headingInputs, ...this.eyeletColourInputs, ...this.fitPositionInputs, ...this.fittingTypeInputs, ...this.liningInputs, ...this.controlSideInputs, ...this.pairingInputs]
        .find((input) => input && !input.checkValidity());

      if (firstInvalid) {
        firstInvalid.reportValidity();
        firstInvalid.focus();
      }
    }

    async handleSampleAction(evt) {
      const button = evt.currentTarget;
      const action = button.dataset.mtmSampleAction;
      const defaultText = button.textContent;

      if (action === 'sample-and-product' && !this.validate()) {
        this.reportValidity();
        return;
      }

      if (action === 'sample-and-product') {
        this.showCartItemPendingMessage();
        return;
      }

      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      button.textContent = 'Adding sample...';

      try {
        await this.addSampleToCart();
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        button.textContent = defaultText;
      }
    }

    async addSampleToCart() {
      const sample = this.getSampleConfig();
      const sampleVariantId = this.normaliseVariantId(sample.sampleVariantId || sample.variantId);

      if (!sampleVariantId) {
        this.showMessage("Sorry, this fabric sample is not connected yet. Please contact us and we'll help you order one.");
        return false;
      }

      const root = window.Shopify?.routes?.root || '/';
      const existingSample = await this.getExistingCartItem(sampleVariantId);
      if (existingSample) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        this.showMessage('Your free sample has been added to your basket.', 'success');
        return true;
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: sampleVariantId,
          quantity: 1,
          properties: this.getSampleProperties(sample)
        })
      });

      if (!response.ok) {
        console.warn('[MTM sample] cart/add.js failed', {
          status: response.status,
          body: await response.json().catch(() => null)
        });
        this.showMessage("Sorry, this fabric sample couldn't be added just now. Please try again.");
        return false;
      }

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      this.showMessage('Your free sample has been added to your basket.', 'success');
      return true;
    }

    getSampleConfig() {
      const mappedSample = this.compactObject(window.MTM_SAMPLE_VARIANTS?.[this.dataset.productHandle] || {});
      return {
        ...(this.config.sample || {}),
        ...mappedSample
      };
    }

    compactObject(value) {
      return Object.fromEntries(
        Object.entries(value).filter(([, entry]) => entry !== null && entry !== undefined && entry !== '')
      );
    }

    getSampleProperties(sample) {
      return {
        'Sample for': this.dataset.productTitle || sample.sampleTitle || sample.fabricName || 'Made-to-measure fabric',
        ...(sample.fabricName ? { Fabric: sample.fabricName } : {}),
        ...(sample.fabricCode ? { 'Fabric code': sample.fabricCode } : {})
      };
    }

    async getExistingCartItem(variantId) {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return null;

      const cart = await response.json().catch(() => null);
      return cart?.items?.find((item) => String(item.variant_id) === String(variantId)) || null;
    }

    async addMtmQuoteToCart() {
      if (!this.quote?.quoteId || !this.quote?.quoteToken) {
        this.showMessage('Please wait for a valid made-to-measure quote before adding this item to your basket.');
        this.updateSubmitButtonState();
        return false;
      }

      if (!this.createCartItemUrl) {
        this.showMessage('Made-to-measure basket setup is not configured yet.');
        return false;
      }

      this.setSubmitLoading(true);

      try {
        const cartItemResponse = await fetch(this.createCartItemUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            quoteId: this.quote.quoteId,
            quoteToken: this.quote.quoteToken,
            productHandle: this.dataset.productHandle
          })
        });
        const cartItem = await cartItemResponse.json().catch(() => null);

        if (!cartItemResponse.ok || !cartItem?.ok) {
          console.warn('Made-to-measure cart item creation failed.', {
            status: cartItemResponse.status,
            code: cartItem?.error?.code || cartItem?.code || null
          });
          if (cartItem?.error?.code === 'quote_expired' || cartItem?.code === 'quote_expired') {
            this.showQuoteExpiredError();
            return false;
          }
          this.showAddToCartError();
          return false;
        }

        const added = await this.addReturnedCartItem(cartItem);
        if (!added) return false;

        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        this.showMessage(this.isRomanBlind()
          ? 'Your made-to-measure Roman blind has been added to your basket.'
          : 'Your made-to-measure curtains have been added to your basket.', 'success');
        return true;
      } catch (error) {
        console.error('Made-to-measure add-to-cart flow failed.', error);
        this.showAddToCartError();
        return false;
      } finally {
        this.setSubmitLoading(false);
      }
    }

    async addReturnedCartItem(cartItem) {
      const root = window.Shopify?.routes?.root || '/';
      const variantId = this.normaliseVariantId(cartItem.variantId);

      if (!variantId) {
        console.warn('Made-to-measure cart item returned an invalid variant ID.', {
          variantId: cartItem.variantId
        });
        this.showAddToCartError();
        return false;
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: cartItem.quantity || 1,
          properties: cartItem.properties || {}
        })
      });
      const cartData = await response.json().catch(() => null);

      if (!response.ok) {
        console.warn('Made-to-measure Shopify cart add failed.', {
          status: response.status,
          description: cartData?.description || cartData?.message || null
        });
        this.showAddToCartError();
        return false;
      }

      return true;
    }

    normaliseVariantId(variantId) {
      if (typeof variantId === 'number' && Number.isFinite(variantId)) return String(Math.trunc(variantId));
      if (typeof variantId !== 'string') return '';

      const trimmed = variantId.trim();
      const gidMatch = trimmed.match(/\/ProductVariant\/(\d+)$/);
      if (gidMatch) return gidMatch[1];
      if (/^\d+$/.test(trimmed)) return trimmed;
      return '';
    }

    setSubmitLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitIsLoading = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
      this.updateSubmitButtonState();
    }

    updateSubmitButtonState() {
      if (!this.submitButton) return;
      const disabled = this.submitIsLoading || !this.quote?.quoteId || !this.quote?.quoteToken;
      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.updateButtonNote(disabled);
    }

    updateButtonNote(disabled) {
      if (!this.buttonNote) return;

      if (!disabled && this.quote?.quoteId && this.quote?.quoteToken) {
        this.buttonNote.textContent = 'Your made-to-measure quote is ready.';
        return;
      }

      if (this.customQuoteButton && !this.customQuoteButton.hidden) {
        this.buttonNote.textContent = 'This size needs a special quote before it can be added to basket.';
        return;
      }

      this.buttonNote.textContent = 'Add to basket is available once your made-to-measure quote is ready.';
    }

    showAddToCartError() {
      this.showMessage("Sorry, we couldn't add this made-to-measure item to your basket just now. Please refresh the quote and try again.");
    }

    showQuoteExpiredError() {
      this.showMessage('This made-to-measure quote has expired. Please refresh the price and try again.');
    }

    async fetchQuote() {
      if (this.quoteAbortController) this.quoteAbortController.abort();
      this.quoteAbortController = new AbortController();

      try {
        const response = await fetch(this.quoteApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(this.getQuotePayload()),
          signal: this.quoteAbortController.signal
        });
        const data = await response.json().catch(() => null);

        if (!response.ok || !data?.success) {
          this.handleQuoteError(data?.error);
          return;
        }

        this.applyQuote(data);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setPriceUnavailable('Quote unavailable', 'The quote service could not be reached. Please try again.');
      }
    }

    handleQuoteError(error = {}) {
      if (error.code === 'size_out_of_range' || error.code === 'matrix_limit_exceeded') {
        this.setPriceUnavailable(
          'Custom quote needed',
          "This size is outside our online made-to-measure range. Please contact us and we'll check whether a special quote is available."
        );
        this.showCustomQuoteRequest();
        return;
      }

      if (error.code === 'single_pricing_requires_custom_quote') {
        this.setPriceUnavailable(
          'Custom quote needed',
          "Single curtain pricing needs a custom check. Please request a special quote and we'll confirm the price for you."
        );
        this.showCustomQuoteRequest();
        return;
      }

      const message = error.message || 'Quote unavailable. Please check your measurements or try again.';
      this.setPriceUnavailable('Quote unavailable', message);
    }

    getQuotePayload() {
      const payload = {
        productHandle: this.dataset.productHandle,
        productType: this.productType,
        widthCm: Number.parseFloat(this.widthInput?.value || 0),
        dropCm: Number.parseFloat(this.dropInput?.value || 0),
        lining: this.getCheckedValue(this.liningInputs)
      };

      if (this.isRomanBlind()) {
        payload.fitPosition = this.getCheckedValue(this.fitPositionInputs);
        payload.fittingType = this.getCheckedValue(this.fittingTypeInputs);
        payload.controlSide = this.getCheckedValue(this.controlSideInputs);
      } else {
        payload.heading = this.getCheckedValue(this.headingInputs);
        payload.pairSingle = (this.getCheckedValue(this.pairingInputs) || '').toLowerCase();
        if (payload.heading === 'eyelet') {
          payload.eyeletColour = this.getCheckedValue(this.eyeletColourInputs);
        }
      }

      return payload;
    }

    hasValidQuoteInputs() {
      const width = Number.parseFloat(this.widthInput?.value || 0);
      const drop = Number.parseFloat(this.dropInput?.value || 0);

      const hasBaseInputs = Number.isFinite(width)
        && width > 0
        && Number.isFinite(drop)
        && drop > 0
        && Boolean(this.getCheckedValue(this.liningInputs));

      if (!hasBaseInputs) return false;

      if (this.isRomanBlind()) {
        return Boolean(this.getCheckedValue(this.fitPositionInputs))
          && Boolean(this.getCheckedValue(this.fittingTypeInputs))
          && Boolean(this.getCheckedValue(this.controlSideInputs));
      }

      return Boolean(this.getCheckedValue(this.headingInputs))
        && (this.getCheckedValue(this.headingInputs) !== 'eyelet' || Boolean(this.getCheckedValue(this.eyeletColourInputs)))
        && Boolean(this.getCheckedValue(this.pairingInputs));
    }

    hasValidMeasurements() {
      const width = Number.parseFloat(this.widthInput?.value || 0);
      const drop = Number.parseFloat(this.dropInput?.value || 0);

      return Number.isFinite(width)
        && width > 0
        && Number.isFinite(drop)
        && drop > 0;
    }

    getIncompleteQuoteMessage() {
      if (!this.hasValidMeasurements()) {
        return this.isRomanBlind()
          ? 'Enter your measurements and choose your fit, lining and control side to see your price.'
          : 'Enter your measurements and choose your heading and lining to see your price.';
      }

      const missing = [];
      if (this.isRomanBlind()) {
        if (!this.getCheckedValue(this.fitPositionInputs)) missing.push('fit position');
        if (!this.getCheckedValue(this.fittingTypeInputs)) missing.push('fitting type');
        if (!this.getCheckedValue(this.controlSideInputs)) missing.push('control side');
      } else {
        if (!this.getCheckedValue(this.headingInputs)) missing.push('heading');
        if (this.getCheckedValue(this.headingInputs) === 'eyelet' && !this.getCheckedValue(this.eyeletColourInputs)) {
          missing.push('eyelet colour');
        }
      }
      if (!this.getCheckedValue(this.liningInputs)) missing.push('lining');

      return missing.length
        ? `Choose your ${this.formatList(missing)} to see your price.`
        : 'Enter valid measurements and choices to calculate a price.';
    }

    formatList(items) {
      if (items.length <= 1) return items[0] || '';
      if (items.length === 2) return `${items[0]} and ${items[1]}`;
      return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
    }

    applyQuote(quote) {
      this.quote = quote;

      if (this.priceEl) this.priceEl.textContent = quote.displayPrice || this.moneyFormatter.format(quote.retailPrice || 0);
      if (this.priceStatusEl) this.priceStatusEl.textContent = this.getPriceExplanation(quote);

      this.setOptionalText(this.comparePriceEl, quote.promotion?.active && quote.displayCompareAtPrice ? `Was ${quote.displayCompareAtPrice}` : '');
      this.setOptionalText(this.savingEl, quote.promotion?.active && quote.displaySaving ? `You save ${quote.displaySaving}` : '');
      this.setOptionalText(this.promotionEl, this.getPromotionText(quote));
      this.hideCustomQuoteLink();

      if (this.priceProperty) this.priceProperty.value = quote.displayPrice || '';
      if (this.quoteIdInput) this.quoteIdInput.value = quote.quoteId || '';
      if (this.quoteTokenInput) this.quoteTokenInput.value = quote.quoteToken || '';
      if (this.expiresAtInput) this.expiresAtInput.value = quote.expiresAt || '';
      if (this.retailPriceInput) this.retailPriceInput.value = quote.retailPrice ?? '';
      if (this.compareAtPriceInput) this.compareAtPriceInput.value = quote.compareAtPrice ?? '';
      if (this.savingInput) this.savingInput.value = quote.saving ?? '';
      if (this.pricingBasisInput) this.pricingBasisInput.value = quote.pricingBasis ? JSON.stringify(quote.pricingBasis) : '';
      this.updateSubmitButtonState();
    }

    getPriceExplanation(quote) {
      const unit = this.config.unit || 'cm';
      const width = this.formatMeasurement(quote.widthCm ?? this.widthInput?.value);
      const drop = this.formatMeasurement(quote.dropCm ?? this.dropInput?.value);
      if (this.isRomanBlindQuote(quote)) {
        const fitPositionValue = quote.fitPosition || this.getCheckedValue(this.fitPositionInputs);
        const fittingTypeValue = quote.fittingType || this.getCheckedValue(this.fittingTypeInputs);
        const liningValue = quote.lining || this.getCheckedValue(this.liningInputs);
        const controlSideValue = quote.controlSide || this.getCheckedValue(this.controlSideInputs);
        const fitPositionLabel = this.getSelectedOptionLabel(this.fitPositionInputs, fitPositionValue);
        const fittingTypeLabel = this.getSelectedOptionLabel(this.fittingTypeInputs, fittingTypeValue);
        const liningLabel = this.getSelectedOptionLabel(this.liningInputs, liningValue);
        const controlSideLabel = this.getSelectedOptionLabel(this.controlSideInputs, controlSideValue);
        return `Price includes one Roman blind made to measure for your window size: ${width}${unit} wide x ${drop}${unit} drop, with ${fitPositionLabel} fit, ${fittingTypeLabel}, ${liningLabel} lining, ${controlSideLabel} control side and free UK delivery.`;
      }

      const headingValue = quote.heading || this.getCheckedValue(this.headingInputs);
      const liningValue = quote.lining || this.getCheckedValue(this.liningInputs);
      const eyeletColourValue = quote.eyeletColour || this.getCheckedValue(this.eyeletColourInputs);
      const headingLabel = this.getSelectedOptionLabel(this.headingInputs, headingValue);
      const liningLabel = this.getSelectedOptionLabel(this.liningInputs, liningValue);

      if (headingValue === 'eyelet' && eyeletColourValue) {
        const eyeletColourLabel = this.getSelectedOptionLabel(this.eyeletColourInputs, eyeletColourValue);
        return `Price includes a pair of curtains made to measure for your window size: ${width}${unit} wide x ${drop}${unit} drop, with ${headingLabel} heading, ${eyeletColourLabel} eyelets, ${liningLabel} lining and free UK delivery.`;
      }

      return `Price includes a pair of curtains made to measure for your window size: ${width}${unit} wide x ${drop}${unit} drop, with ${headingLabel} heading, ${liningLabel} lining and free UK delivery.`;
    }

    isRomanBlind() {
      return this.productType === 'roman_blind';
    }

    isRomanBlindQuote(quote) {
      return quote.productType === 'roman_blind' || this.isRomanBlind();
    }

    getSelectedOptionLabel(inputs, fallbackValue) {
      const selected = [...inputs].find((input) => input.checked) || [...inputs].find((input) => input.value === fallbackValue);
      return this.getOptionLabel(selected) || this.formatOptionValue(fallbackValue);
    }

    formatOptionValue(value = '') {
      return String(value)
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }

    formatMeasurement(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return value;
      return Number.isInteger(number) ? String(number) : String(number).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    }

    getPromotionText(quote) {
      if (!quote.promotion?.active) return '';

      const parts = [quote.promotion.label].filter(Boolean);
      if (quote.promotion.endsAt) {
        parts.push(`Ends ${this.formatDate(quote.promotion.endsAt)}`);
      }

      return parts.join(' - ');
    }

    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).format(date);
    }

    setPriceLoading() {
      if (this.priceEl) this.priceEl.textContent = 'Calculating price...';
      if (this.priceStatusEl) this.priceStatusEl.textContent = 'Calculating your made-to-measure price.';
      this.setOptionalText(this.comparePriceEl, '');
      this.setOptionalText(this.savingEl, '');
      this.setOptionalText(this.promotionEl, '');
      this.hideCustomQuoteLink();
    }

    setPriceUnavailable(priceText, statusText) {
      if (this.priceEl) this.priceEl.textContent = priceText;
      if (this.priceStatusEl) this.priceStatusEl.textContent = statusText;
      this.setOptionalText(this.comparePriceEl, '');
      this.setOptionalText(this.savingEl, '');
      this.setOptionalText(this.promotionEl, '');
      this.hideCustomQuoteLink();
    }

    setOptionalText(element, value) {
      if (!element) return;
      element.hidden = !value;
      element.textContent = value || '';
    }

    clearQuote() {
      this.quote = null;
      if (this.priceProperty) this.priceProperty.value = '';
      if (this.quoteIdInput) this.quoteIdInput.value = '';
      if (this.quoteTokenInput) this.quoteTokenInput.value = '';
      if (this.expiresAtInput) this.expiresAtInput.value = '';
      if (this.retailPriceInput) this.retailPriceInput.value = '';
      if (this.compareAtPriceInput) this.compareAtPriceInput.value = '';
      if (this.savingInput) this.savingInput.value = '';
      if (this.pricingBasisInput) this.pricingBasisInput.value = '';
      this.updateSubmitButtonState();
    }

    showCustomQuoteRequest() {
      if (!this.customQuoteButton) return;
      this.populateCustomQuoteForm();
      this.customQuoteButton.hidden = false;
      this.updateSubmitButtonState();
    }

    expandCustomQuoteForm() {
      this.populateCustomQuoteForm();
      if (this.customQuoteForm) {
        this.customQuoteForm.hidden = false;
        this.customQuoteForm.classList.remove('is-hidden');
      }
      if (this.customQuoteButton) this.customQuoteButton.hidden = true;
    }

    populateCustomQuoteForm() {
      if (!this.customQuoteForm) return;

      const payload = this.getQuotePayload();
      const unit = this.config.unit || 'cm';
      const headingLabel = this.getOptionLabel([...this.headingInputs].find((input) => input.checked));
      const eyeletColourLabel = payload.heading === 'eyelet'
        ? this.getOptionLabel([...this.eyeletColourInputs].find((input) => input.checked))
        : '';
      const fitPositionLabel = this.getOptionLabel([...this.fitPositionInputs].find((input) => input.checked));
      const fittingTypeLabel = this.getOptionLabel([...this.fittingTypeInputs].find((input) => input.checked));
      const liningLabel = this.getOptionLabel([...this.liningInputs].find((input) => input.checked));
      const controlSideLabel = this.getOptionLabel([...this.controlSideInputs].find((input) => input.checked));
      const pairSingleLabel = this.getOptionLabel([...this.pairingInputs].find((input) => input.checked));
      const productUrl = this.dataset.productUrl
        ? new URL(this.dataset.productUrl, window.location.origin).toString()
        : window.location.href;
      const requestedAt = new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });

      this.setContactField('[data-mtm-contact-product-title]', this.dataset.productTitle || '');
      this.setContactField('[data-mtm-contact-product-handle]', this.dataset.productHandle || '');
      this.setContactField('[data-mtm-contact-product-url]', productUrl);
      this.setContactField('[data-mtm-contact-width]', payload.widthCm ? `${payload.widthCm}${unit}` : '');
      this.setContactField('[data-mtm-contact-drop]', payload.dropCm ? `${payload.dropCm}${unit}` : '');
      this.setContactField('[data-mtm-contact-heading]', headingLabel);
      this.setContactField('[data-mtm-contact-eyelet-colour]', eyeletColourLabel);
      this.setContactField('[data-mtm-contact-fit-position]', fitPositionLabel);
      this.setContactField('[data-mtm-contact-fitting-type]', fittingTypeLabel);
      this.setContactField('[data-mtm-contact-lining]', liningLabel);
      this.setContactField('[data-mtm-contact-control-side]', controlSideLabel);
      this.setContactField('[data-mtm-contact-pair-single]', pairSingleLabel);
      this.setContactField('[data-mtm-contact-requested-at]', requestedAt);

      if (this.customQuoteSummary) {
        const summary = [
          this.dataset.productTitle || '',
          payload.widthCm && payload.dropCm ? `${payload.widthCm}${unit} width x ${payload.dropCm}${unit} drop` : '',
          this.isRomanBlind() ? fitPositionLabel : headingLabel,
          this.isRomanBlind() ? fittingTypeLabel : eyeletColourLabel,
          liningLabel,
          this.isRomanBlind() ? controlSideLabel : pairSingleLabel
        ].filter(Boolean);

        this.customQuoteSummary.textContent = summary.join(' - ');
      }
    }

    setContactField(selector, value) {
      const field = this.querySelector(selector);
      if (field) field.value = value || '';
    }

    hideCustomQuoteLink() {
      if (this.customQuoteButton) this.customQuoteButton.hidden = true;
      if (this.customQuoteForm) {
        this.customQuoteForm.hidden = true;
        this.customQuoteForm.classList.add('is-hidden');
      }
    }

    showCartItemPendingMessage() {
      if (this.quote) {
        this.showMessage('Your made-to-measure quote is ready.', 'success');
        return;
      }

      if (!this.quoteApiUrl) {
        this.showMessage('Quote API is not configured yet.');
        return;
      }

      this.showMessage(this.isRomanBlind()
        ? 'A valid quote is required before adding a made-to-measure Roman blind to basket.'
        : 'A valid quote is required before adding made-to-measure curtains to basket.');
    }

    showMessage(message, tone = 'error') {
      if (!this.messageEl) return;

      this.messageEl.hidden = false;
      this.messageEl.textContent = message;
      this.messageEl.classList.toggle('bg-error-bg', tone === 'error');
      this.messageEl.classList.toggle('text-error-text', tone === 'error');
      this.messageEl.classList.toggle('bg-success-bg', tone === 'success');
      this.messageEl.classList.toggle('text-success-text', tone === 'success');
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.hidden = true;
      this.messageEl.textContent = '';
    }
  }

  customElements.define('mtm-calculator', MtmCalculator);
}
